/**
 * Event Card Component
 *
 * Dedicated component for 'event' resource items (concerts, festivals, shows, etc.).
 * Supports both feed card and map popup rendering via the `variant` prop.
 * Renders event-specific metadata: formatted date/time, venue, organizer, admission,
 * recurrence badge, contacts, amenities and contextual action links.
 */

import { h } from 'preact';
import htm from 'htm';
import { IconMapPin, IconCalendar } from '../../../components/Icons.js';
import { CardMedia } from './shared/CardMedia.js';
import { CardDescription } from './shared/CardDescription.js';
import { CardLinks } from './shared/CardLinks.js';
import { CardChips } from './shared/CardChips.js';
import { pickText, pickList, label } from './shared/fields.js';
import { formatEventDateTime } from '../../../utils/dateFormatters.js';
import { buildLocationLabel } from '../../../utils/locationLabel.js';

const html = htm.bind(h);

/**
 * Renders an event item as a result card or map popup.
 *
 * @param {Object} props
 * @param {Object} props.item - The event item data.
 * @param {Object} [props.block] - Parent block data.
 * @param {Object} props.ctx - View context with `t`, `config`, and `onShowPinOnMap`.
 * @param {string} [props.variant='card'] - 'card' for blocks feed or 'popup' for map infowindow.
 * @param {Function} [props.onClose] - Callback when the popup close button is clicked.
 */
export function EventCard({ item, block, ctx, variant = 'card', onClose }) {
  const prof = item?.profile || {};
  const isPopup = variant === 'popup';

  const tag = pickText(item, 'categories', 'category', 'tag') || label(ctx, 'type_event', 'Event');
  const recurrence = pickText(item, 'recurrence');
  const name = pickText(item, 'name', 'label');
  const formattedDate = formatEventDateTime(item);
  const locationLabel = buildLocationLabel(item);

  const latitude = prof.latitude ?? item?.latitude;
  const longitude = prof.longitude ?? item?.longitude;
  const isClickableLocation = !isPopup && latitude != null && longitude != null && typeof ctx?.onShowPinOnMap === 'function';

  const organizer = pickText(item, 'organizer_name');
  const admission = pickText(item, 'admission');
  const phone = pickText(item, 'phone');
  const email = pickText(item, 'email');
  const description = pickText(item, 'description');
  const amenities = pickList(item, 'amenities');
  const website = pickText(item, 'website');
  const bookingUrl = pickText(item, 'booking_url');
  const source_url = pickText(item, 'source_url');
  const map_url = pickText(item, 'map_url');

  const handleLocationClick = (e) => {
    if (isClickableLocation) {
      e.preventDefault();
      ctx.onShowPinOnMap({
        points: [
          {
            ...item,
            category: tag,
            latitude: latitude,
            longitude: longitude,
            image_url: item?.image_url || pickText(item, 'photos') || block?.image_url
          }
        ],
        center_latitude: latitude,
        center_longitude: longitude,
        zoom: 15
      });
    }
  };

  const containerClass = isPopup
    ? 'mta-result-card mta-event-card mta-card--popup mta-map-popup-card mta-map-popup-theme'
    : 'mta-result-card mta-event-card';

  return html`
    <div class="${containerClass}">
      <${CardMedia} item=${item} block=${block} ctx=${ctx} variant=${variant} onClose=${onClose} />

      <div class="mta-card-tag-row">
        <div class="mta-card-tag">${tag}</div>
        ${recurrence ? html`<span class="mta-card-recurrence-badge">${recurrence}</span>` : ''}
      </div>

      <h3 class="mta-card-title">${name}</h3>

      <div class="mta-card-meta">
        ${formattedDate ? html`
          <div class="mta-card-meta-item mta-meta-date" title="Event Date & Time">
            <span style="display:inline-flex; align-items:center; color:var(--mta-text-soft);"><${IconCalendar} /></span>
            <span>${formattedDate}</span>
          </div>
        ` : ''}

        ${locationLabel ? html`
          <div
            class=${`mta-card-meta-item ${isClickableLocation ? 'mta-clickable-address' : ''}`}
            onClick=${handleLocationClick}
            title="Event Venue"
          >
            <span class="mta-item-pin-icon"><${IconMapPin} /></span>
            <span>${locationLabel}</span>
          </div>
        ` : ''}

        ${organizer ? html`
          <div class="mta-card-meta-item" title="Organizer">
            <span>${label(ctx, 'organized_by', 'Organized by {name}').replace('{name}', organizer)}</span>
          </div>
        ` : ''}

        ${admission ? html`
          <div class="mta-card-meta-item mta-meta-admission" title="Admission">
            <span>🎟️ ${
              /^https?:\/\//i.test(admission)
                ? html`<a href="${admission}" target="_blank" rel="noopener noreferrer" style="color: inherit; text-decoration: underline;">${label(ctx, 'get_tickets', 'Get Tickets')}</a>`
                : admission
            }</span>
          </div>
        ` : ''}

        ${phone ? html`
          <div class="mta-card-meta-item" title="Phone">
            <span>📞 ${phone}</span>
          </div>
        ` : ''}

        ${email ? html`
          <div class="mta-card-meta-item" title="Email">
            <span>✉️ <a href="mailto:${email}" style="color: inherit;">${email}</a></span>
          </div>
        ` : ''}
      </div>

      <${CardDescription} description=${description} ctx=${ctx} variant=${variant} />

      ${!isPopup ? html`<${CardChips} items=${amenities} />` : ''}

      <${CardLinks} links=${[
        { href: map_url, text: label(ctx, 'show_on_map', 'Show on map') },
        { href: bookingUrl, text: label(ctx, 'get_tickets', 'Get Tickets') },
        { href: website, text: label(ctx, 'event_website', 'Event Website') },
        { href: source_url, text: label(ctx, 'show_on_site', 'Show on site') }
      ]} />
    </div>
  `;
}
